import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, TableModel, TableRow } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useHistory } from 'react-router';
import colors from 'assets/scss/colors.module.scss';
import { AgtFitnessVm } from 'app/stores/ui/agt.fitness.ui.store';
import { Icon } from 'app/components/common/Icon';
import { formatNumTwoDig } from 'app/utils';
import { AgtAkumaPlanerMultiGp26Detail } from './AgtAkumaPlanerMultiGp26Detail';
import { init, EChartsOption } from 'echarts';

export const AkumaMultiGpList = observer(() => {
	const { berichteUiiStore, agtMultiGpUiStore, agenturListUiStore, agenturFilterUiStore, session } = useStore();
	const location = useLocation();
	const history = useHistory();
	const [hasItems, setHasItems] = useState(false);
	const [histogramData, setHistogramData] = useState<{ kws: string; count: number }[]>([]);
	const [akumaZiel, setAkumaZiel] = useState<number | null>(null);
	const chartRef = useRef<HTMLDivElement>(null);

	const selectedAgtId = useMemo(() => {
		const params = new URLSearchParams(location.search);
		const id = params.get('detailAgtId');
		return id ? parseInt(id) : null;
	}, [location.search]);

	const tm = useMemo(() => {
		const tm = new TableModel<AgtFitnessVm, number>();
		const cols: ITableColumn<AgtFitnessVm, number>[] = [];
		cols.push({
			label: 'Agentur',
			path: 'agt.name',
			sortBy: 'agt.name',
			render: AgenturNameCell,
		});

		cols.push({
			label: 'GP',
			path: 'akumaMultiGpCount',
			render(row) {
				return <>{row.data.akumaMultiGpCount > 0 ? <Icon iconClass="check-circle" faModifier="far" /> : <></>}</>;
			},
			agg: [
				{
					aggType: AggregrationType.sum,
				},
			],
		});
		cols.push({
			label: 'Teilhaber',
			path: `agt.thCount`,
			agg: [
				{
					aggType: AggregrationType.sum,
				},
			],
		});

		cols.push({
			label: 'Prime wird genutzt',
			path: `isAkumaPrimeUser`,
			sortBy: `isAkumaPrimeUser`,
			cellModifier: 'border-left-yellow bg-yellow',
			labelModifier: 'border-left-yellow bg-yellow',
			render(row) {
				return <>{row.data.isAkumaPrimeUser ? <Icon iconClass="check-circle" /> : <></>}</>;
			},
			agg: {
				aggType: AggregrationType.count,
			},
		});

		cols.push({
			label: 'Für Prime angemeldet',
			path: `isCurrentAkumaPrimeUser`,
			sortBy: `isCurrentAkumaPrimeUser`,
			cellModifier: 'bg-yellow',
			labelModifier: 'bg-yellow',
			render(row) {
				return <>{row.data.isCurrentAkumaPrimeUser ? <Icon iconClass="check-circle" /> : <></>}</>;
			},
			agg: {
				aggType: AggregrationType.count,
				path: 'isCurrentAkumaPrimeUser',
			},
		});

		cols.push({
			label: 'AKUMA Planer GP Wochen',
			path: `planer.kwsAbove50Count`,
			sortBy: `planer.kwsAbove50Count`,
			labelModifier: 'border-left-yellow bg-yellow',
			cellModifier: 'border-left-yellow bg-yellow',
			agg: {
				aggType: AggregrationType.avg,
				format: formatNumTwoDig,
			},
		});

		cols.push({
			label: '12 Wochen Übertragungsquote mind. 50%',
			path: `has12WeeksOfPlanerInCurrentYear`,
			sortBy: `has12WeeksOfPlanerInCurrentYear`,
			cellModifier: 'bg-yellow',
			labelModifier: 'bg-yellow',
			render(row) {
				return <>{row.data.has12WeeksOfPlanerInCurrentYear ? <Icon iconClass="check-circle" /> : <></>}</>;
			},
			agg: {
				aggType: AggregrationType.count,
			},
		});

		tm.sortBy = 'agt.name';
		tm.setCols(cols);

		tm.idProperty = 'agtId';
		tm.idType = 'number';

		tm.onRowClick = (row: TableRow<AgtFitnessVm>) => {
			const params = new URLSearchParams(history.location.search);
			params.set('detailAgtId', row.data.agt.agtId.toString());
			history.replace(history.location.pathname + '?' + params.toString());
		};

		return tm;
	}, []);

	useEffect(() => {
		agtMultiGpUiStore.load().then(() => {
			let items = agtMultiGpUiStore.fitnessItems;
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				items = items.filter((i) => i.agt.agtId === agtId);
			} else {
				items = agenturListUiStore.applyFilterOnAgt(items) as AgtFitnessVm[];
				items = agenturListUiStore.applyFilterOnGpRelevantAgt(items) as AgtFitnessVm[];
			}
			setHasItems(items.length > 0);
			tm.setRowData(items);
			tm.sort();

			// Build histogram: count of agencies per kwsAbove50Count (skip 0, cap at 12+)
			const counts = new Map<number, number>();
			for (const item of items) {
				const kws = Math.min(item.planer?.kwsAbove50Count || 0, 12);
				if (kws === 0) continue;
				counts.set(kws, (counts.get(kws) || 0) + 1);
			}
			const histogram: { kws: string; count: number }[] = [];
			for (let i = 1; i <= 12; i++) {
				histogram.push({ kws: i === 12 ? '12+' : String(i), count: counts.get(i) || 0 });
			}
			setHistogramData(histogram);

			// Resolve VD for Ziel: user's VD (multi) or VD filter
			let vdId: string | undefined;
			const vdFilter = agenturFilterUiStore._filters.find((f) => f.name === 'vd');
			if (vdFilter && vdFilter.hasValue && vdFilter.value.length === 1) {
				vdId = vdFilter.value[0];
			} else if (session.isMulti && session.currentUser?.vdList?.length) {
				vdId = session.currentUser.vdList[0];
			}

			if (vdId) {
				const multiGp = agtMultiGpUiStore.items.find((m) => m.vd === vdId);
				if (multiGp) {
					setAkumaZiel(multiGp.akumaZiel);
				}
			} else {
				setAkumaZiel(null);
			}
		});
	}, [tm, berichteUiiStore.selectedAgt, agenturListUiStore.filterHasChanged, agenturListUiStore, agtMultiGpUiStore]);

	const handleCloseDetail = useCallback(() => {
		const params = new URLSearchParams(history.location.search);
		params.delete('detailAgtId');
		const newSearch = params.toString();
		history.replace(history.location.pathname + (newSearch ? '?' + newSearch : ''));
	}, [history]);

	// ECharts rendering effect
	useEffect(() => {
		if (!chartRef.current || histogramData.length === 0) return;

		const chart = init(chartRef.current);

		// Prepare data
		const xAxisData = histogramData.map((d) => d.kws);
		const countData = histogramData.map((d) => d.count);

		// Calculate dynamic y-axis max
		const dataMax = Math.max(...countData);
		const yAxisMax = Math.ceil(Math.max(dataMax, akumaZiel ?? 0) * 1.1);

		// Find the index of "12+" for the reference line
		const targetIndex = histogramData.findIndex((d) => d.kws === '12+');

		// Build mark lines
		const markLineData: any[] = [
			{
				xAxis: targetIndex,
				lineStyle: {
					color: colors.orange,
					type: [6, 3] as any,
					width: 2,
				},
				label: {
					formatter: 'GP Ziel (12+ Wochen)',
					position: 'top',
					color: colors.orange,
					fontSize: 11,
				},
			},
		];

		if (akumaZiel != null) {
			markLineData.push({
				yAxis: akumaZiel,
				lineStyle: {
					color: 'red',
					type: [4, 2] as any,
					width: 2,
				},
				label: {
					formatter: `Ziel: ${akumaZiel}`,
					position: 'insideEndTop',
					color: 'red',
					fontSize: 11,
				},
			});
		}

		const option: EChartsOption = {
			animation: false,
			tooltip: {
				trigger: 'axis',
				formatter: (params: any) => {
					if (!Array.isArray(params) || params.length === 0) return '';
					const point = params[0];
					return `${point.axisValue} Wochen<br/>${point.marker} Agenturen: ${point.value}`;
				},
			},
			grid: {
				left: 60,
				right: 100,
				top: 30,
				bottom: 50,
				containLabel: false,
			},
			xAxis: {
				type: 'category',
				data: xAxisData,
				name: 'Wochen',
				nameLocation: 'middle',
				nameGap: 25,
			},
			yAxis: {
				type: 'value',
				min: 0,
				max: yAxisMax,
				name: 'Agenturen',
				nameLocation: 'middle',
				nameGap: 40,
				nameTextStyle: {
					fontSize: 12,
				},
			},
			series: [
				{
					name: 'Agenturen',
					type: 'bar',
					data: countData.map((value, index) => ({
						value,
						itemStyle: {
							color: histogramData[index].kws === '12+' ? colors.green : colors.primary,
						},
					})),
					markLine: {
						silent: true,
						symbol: 'none',
						data: markLineData,
					},
				},
			],
		};

		chart.setOption(option);

		const handleResize = () => {
			chart.resize();
		};
		window.addEventListener('resize', handleResize);

		return () => {
			chart.dispose();
			window.removeEventListener('resize', handleResize);
		};
	}, [histogramData, akumaZiel]);

	return (
		<>
			<div className="table-container">
				{!hasItems ? (
					<div className="pad-1rem">Keine Daten gefunden</div>
				) : (
					<>
						{histogramData.length > 0 && (
							<div className="pad-1rem pad-top-3rem">
								<div className="is-centered">Verteilung: Anzahl Agenturen nach Wochen mit Quote {'>='} 50%</div>
								<div ref={chartRef} style={{ width: '100%', height: 200 }} />
							</div>
						)}
						<Table stickyHeader={true} stickyFooter={true} tm={tm}></Table>
					</>
				)}
			</div>
			{selectedAgtId && <AgtAkumaPlanerMultiGp26Detail agtId={selectedAgtId} onClose={handleCloseDetail} />}
		</>
	);
});

export const AkumaByVdList = observer(() => {
	const { berichteUiiStore, agtMultiGpUiStore, agenturListUiStore } = useStore();
	const [hasItems, setHasItems] = useState(false);

	const tm = new TableModel<AgtFitnessVm, number>();
	const cols: ITableColumn<AgtFitnessVm, number>[] = [];
	cols.push({
		label: 'Agentur',
		path: 'agt.vdId',
		sortBy: 'agt.vdId',
	});

	cols.push({
		label: 'isAkumaPrimeUser',
		path: `isAkumaPrimeUser`,
		sortBy: `isAkumaPrimeUser`,
		cellModifier: 'border-left-green bg-green',
		agg: [
			{
				aggType: AggregrationType.sum,
			},
		],
	});

	cols.push({
		label: 'isCurrentAkumaPrimeUser',
		path: `isCurrentAkumaPrimeUser`,
		sortBy: `isCurrentAkumaPrimeUser`,
		agg: [
			{
				aggType: AggregrationType.sum,
			},
		],
	});

	cols.push({
		label: 'isCurrentAkumaPrimeUserGpCount',
		path: `isCurrentAkumaPrimeUserGpCount`,
		sortBy: `isCurrentAkumaPrimeUserGpCount`,
		agg: [
			{
				aggType: AggregrationType.sum,
			},
		],
	});

	cols.push({
		label: '	',
		path: `has12WeeksOfPlanerInCurrentYear`,
		sortBy: `has12WeeksOfPlanerInCurrentYear`,
		agg: [
			{
				aggType: AggregrationType.sum,
			},
		],
	});

	cols.push({
		label: 'akumaPlanerGpCount',
		path: `akumaPlanerGpCount`,
		sortBy: `akumaPlanerGpCount`,
		agg: [
			{
				aggType: AggregrationType.sum,
			},
		],
	});

	tm.sortBy = 'agt.name';
	tm.setCols(cols);

	tm.idProperty = 'agtId';
	tm.idType = 'number';

	useEffect(() => {
		agtMultiGpUiStore.load().then(() => {
			let items = agtMultiGpUiStore.fitnessItems;
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				items = items.filter((i) => i.agt.agtId === agtId);
			} else {
				items = agenturListUiStore.applyFilterOnAgt(items) as AgtFitnessVm[];
			}
			setHasItems(items.length > 0);
			tm.setGroupByData(items, (x) => x.agt.vdId);
			tm.sort();
		});
	}, [tm, berichteUiiStore.selectedAgt, agenturListUiStore.filterHasChanged, agenturListUiStore, agtMultiGpUiStore]);

	return (
		<>
			{!hasItems ? (
				<div className="pad-1rem">Keine Daten gefunden</div>
			) : (
				<>
					<Table stickyHeader={true} tm={tm}></Table>
				</>
			)}
			<ul>
				<li>isAkumaPrimeUser: Definition für AzD (war in letzter zeit mal mitglied bei AKUMA Prime)</li>
				<li>isCurrentAkumaPrimeUser: ist in der letzten Datenlieferung AKUMA Prime mitglied</li>
				<li>isCurrentAkumaPrimeUserGpCount: anzahl teilhaber pro agentur mit isCurrentAkumaPrimeUser</li>
				<li>has12WeeksOfPlanerInCurrentYear: agentur die Im aktuellen jahr mindestens 12 wochen mit mindestens 50 quote</li>
				<li>akumaPlanerGpCount: has12WeeksOfPlanerInCurrentYear mit anzahl telihaber</li>
			</ul>
		</>
	);
});
